import { UsStates } from './us-states';

/**
 * The states / provinces a business address can use. LincSell supports two countries today: US (states) and PK
 * (provinces, ISO 3166-2:PK codes). Entries carry both `code` and `abbreviation` because the app has two
 * shapes in use ({name, code} in the setup checklist, {name, abbreviation} in General and Locations).
 */
export type SupportedCountry = 'US' | 'PK';

export interface StateOption {
  name: string;
  code: string;
  abbreviation: string;
}

const option = (name: string, code: string): StateOption => ({ name, code, abbreviation: code });

export const US_STATE_OPTIONS: StateOption[] = UsStates.map((s) => option(s.name, s.abbreviation));

export const PK_PROVINCE_OPTIONS: StateOption[] = [
  option('Azad Jammu and Kashmir', 'JK'),
  option('Balochistan', 'BA'),
  option('Gilgit-Baltistan', 'GB'),
  option('Islamabad Capital Territory', 'IS'),
  option('Khyber Pakhtunkhwa', 'KP'),
  option('Punjab', 'PB'),
  option('Sindh', 'SD')
];

/** "PK", "pk" and "Pakistan" are Pakistan; anything else (including nothing) is the US. */
export function normalizeCountry(country: string | null | undefined): SupportedCountry {
  const value = String(country || '').trim().toUpperCase();
  return value === 'PK' || value === 'PAKISTAN' ? 'PK' : 'US';
}

/** The country of a business-setup record (either casing), falling back to `fallback`. */
export function countryOfBusiness(setup: any, fallback?: string | null): SupportedCountry {
  return normalizeCountry(setup?.country ?? setup?.Country ?? fallback);
}

export function statesFor(country: string | null | undefined): StateOption[] {
  return normalizeCountry(country) === 'PK' ? PK_PROVINCE_OPTIONS : US_STATE_OPTIONS;
}

/** The wording an address form uses for this country. */
export function regionLabels(country: string | null | undefined): { state: string; zip: string; zipPlaceholder: string } {
  return normalizeCountry(country) === 'PK'
    ? { state: 'Province', zip: 'Postal code', zipPlaceholder: 'Postal code' }
    : { state: 'State', zip: 'ZIP code', zipPlaceholder: '78701' };
}

/** Pakistan postal codes are not validated (they are often missing from addresses); US ZIPs are 5 digits. */
export function zipIsRequired(country: string | null | undefined): boolean {
  return normalizeCountry(country) === 'US';
}
